import React, { useEffect, useMemo, useState } from "react";
import { PHASE_KEYS, caseFilterKey, caseState, stepOf } from "@/lib/stages";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ukDay, ukDayTime } from "@/lib/time";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FINANCE_TYPES } from "./constants";
import { MoneyField, DEAL_PRESETS } from "@/components/ui/number-fields";
import { PurgeButton } from "@/components/hub/PurgeButton";
import { AddedFilter } from "@/components/portal/crm/AddedFilter";
import { inRange, matchesCase, newestFirst, rangeWords } from "@/lib/caseList";
import { DocsProgress } from "@/components/portal/crm/DocsProgress";
import { CompanyLookup } from "@/components/portal/lookup/CompanyLookup";
import { DirectorPick } from "@/components/portal/lookup/DirectorPick";
import { fillEmpty } from "@/lib/lookups";
import { FastTrackTag } from "@/components/portal/crm/FastTrackTag";
import { CaseStamp } from "@/components/portal/crm/CaseStamp";
import {
  Btn, CellMain, CellSub, Chips, Empty, Money, PageHead, Panel, SearchBox, StatusChip, Tag, Tb, Td, Th, openRow,
} from "@/components/portal/crm/kit";

/* Leads — mockup 08's case table, on the whole book rather than the desk's shortlist. */

const STAGES = [
  { key: "live", label: "Live book" },
  { key: "all", label: "All" },
  { key: "new", label: "Submitted", tone: "ink" },
  { key: "sent", label: "With lenders", tone: "ink" },
  { key: "in_review", label: "Under assessment", tone: "blue" },
  { key: "awaiting_docs", label: "Awaiting documents", tone: "amber" },
  { key: "preparing_offer", label: "Preparing offer", tone: "teal" },
  { key: "offer_made", label: "Offer issued", tone: "mint" },
  { key: "offer_accepted", label: "Offer accepted", tone: "acc" },
  { key: "completed", label: "Funded", tone: "green" },
  { key: "declined", label: "Declined", tone: "red" },
  { key: "archived", label: "Archived" },
];
const STAGE_KEYS = ["new", "sent", "in_review", "offer_made", "completed", "declined"];
const accepted = (r) => r.stage === "offer_made" && (r.routes || []).some((x) => stepOf(x) === "offer_accepted");

/** What happened, in the row — not three clicks away. */
const PassedNote = ({ lead }) => {
  if (lead.stage !== "declined") return null;
  const closed = (lead.routes || []).filter((r) => r.status === "declined");
  const passed = closed.filter((r) => !r.withdrawn);
  if (!closed.length) return null;
  const reasons = [...new Set(passed.map((r) => r.decline_reason).filter(Boolean))];
  return (
    <CellSub style={{ marginTop: 5 }}>
      <span data-testid={`hub-lead-passed-${lead.id}`}>
        {passed.length ? `${passed.length} desk${passed.length === 1 ? "" : "s"} passed` : "We took it back"}
        {reasons.length ? ` · ${reasons.slice(0, 2).join(" · ")}` : ""}
        {reasons.length > 2 ? ` +${reasons.length - 2}` : ""} — needs a new home
      </span>
    </CellSub>
  );
};

/** Flags a deal the customer completed themselves. */
const CustomerTag = ({ lead }) => {
  if (!lead?.customer_submitted) return null;
  const pending = lead.review_status === "pending";
  return (
    <span data-testid={`lead-customer-chip-${lead.id}`}>
      <Tag amber={pending}>{pending ? "Needs review" : "Customer applied"}</Tag>
    </span>
  );
};

/** Sent in by an introducer's own system — never auto-routed, so a fresh one waits for the desk. */
const ApiTag = ({ lead }) => {
  if (lead?.submitted_via !== "api" && !lead?.duplicate_of) return null;
  return (
    <>
      {lead.submitted_via === "api" && (
        <span data-testid={`lead-api-chip-${lead.id}`} title="Sent in through the introducer API — route it by hand">
          <Tag amber={lead.stage === "new"}>{lead.stage === "new" ? "API · route by hand" : "API"}</Tag>
        </span>
      )}
      {lead.duplicate_of && (
        <span data-testid={`lead-duplicate-chip-${lead.id}`}
              title={`Same ${(lead.duplicate_reason || "email").replace("+", " & ")} as an earlier case`}>
          <Tag amber>Possible duplicate</Tag>
        </span>
      )}
    </>
  );
};

/** Applied through our own funding page — no introducer behind it, no commission on it. */
const DirectTag = ({ lead }) => {
  if (!lead?.direct) return null;
  return (
    <span data-testid={`lead-direct-chip-${lead.id}`}
          title="Applied through connex.is — no introducer, no commission payable">
      <Tag>Direct</Tag>
    </span>
  );
};

export default function HubLeads() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [added, setAdded] = useState({ key: "any" });
  const [stage, setStage] = useState("all");
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState(false);
  const [asOf, setAsOf] = useState(null);
  const [archivedCount, setArchivedCount] = useState(0);

  const load = async (nextStage = stage) => {
    setLoading(true);
    try {
      const res = await api.get("/hub/leads", {
        params: { stage: ["all", "live"].includes(nextStage) ? (nextStage === "live" ? "live" : undefined)
          : PHASE_KEYS.includes(nextStage) ? "in_review" : nextStage, limit: 300 },
      });
      setRows(res.data.items || []);
      setAsOf(new Date().toISOString());
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(stage); }, [stage]);
  useEffect(() => {
    api.get("/hub/archive", { params: { limit: 500 } })
      .then((r) => setArchivedCount(r.data.total || 0)).catch(() => {});
  }, [stage]);

  const windowed = useMemo(() => newestFirst(rows).filter((r) => inRange(r.created_at, added)), [rows, added]);

  const counts = useMemo(() => {
    const c = { all: windowed.length, live: windowed.filter((r) => !["completed", "declined"].includes(r.stage)).length,
                archived: archivedCount };
    STAGE_KEYS.forEach((k) => { c[k] = windowed.filter((r) => r.stage === k).length; });
    PHASE_KEYS.forEach((k) => { c[k] = windowed.filter((r) => caseFilterKey(r) === k).length; });
    c.offer_accepted = windowed.filter(accepted).length;
    return c;
  }, [windowed, archivedCount]);

  const shown = useMemo(() => windowed
    .filter((l) => !PHASE_KEYS.includes(stage) || caseFilterKey(l) === stage)
    .filter((l) => matchesCase(l, q, [l.source_name, l.submitted_by_name])), [windowed, q, stage]);

  return (
    <div data-testid="hub-leads">
      <PageHead
        eyebrow="Leads"
        title="The whole book"
        lede="Every case we hold, whoever introduced it and wherever it sits. Tap a row to open the case record."
        testId="hub-leads-head"
        actions={
          <>
            {sel.length > 0 && (
              <span className="st st-rev" data-testid="hub-leads-bulk-bar">
                <i className="sq" />{sel.length} selected
                <button onClick={() => setSel([])} data-testid="hub-leads-clear-sel"
                        style={{ border: 0, background: "none", color: "inherit", cursor: "pointer", marginLeft: 8, textDecoration: "underline" }}>
                  clear
                </button>
              </span>
            )}
            {sel.length > 0 && <PurgeButton kind="lead" ids={sel} onDone={() => { setSel([]); load(); }} testId="hub-leads-purge" />}
            <Btn kind="primary" onClick={() => setOpen(true)} testId="hub-lead-new">New case</Btn>
          </>
        }
      />

      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <SearchBox value={q} onChange={setQ} placeholder="Search name, business, email, phone or reference" testId="hub-leads-search" />
        <AddedFilter value={added} onChange={setAdded} testId="hub-leads-added" />
        <div style={{ marginLeft: "auto", minWidth: 0, maxWidth: "100%" }}>
          <Chips items={STAGES.map((s) => ({ ...s, count: counts[s.key] ?? 0 }))}
                 value={stage} onChange={setStage} testPrefix="hub-leads-stage" />
        </div>
      </div>

      <Panel pad={false} testId="hub-leads-panel" style={{ overflow: "hidden" }}
             footer={
               <>
                 <span>Stages are computed from every route on the case — a manual override on the case record always wins.</span>
                 <span style={{ fontWeight: 700, color: "var(--ink-3)" }}>{asOf ? `${ukDayTime(asOf)} UK` : ""}</span>
               </>
             }>
        {loading ? (
          <Empty testId="hub-leads-loading">Reading the book…</Empty>
        ) : shown.length === 0 ? (
          <Empty testId="hub-leads-empty">
            {rows.length === 0 ? "No cases at this stage yet."
              : windowed.length === 0 ? `No cases added ${rangeWords(added)}.` : "Nothing matches that search."}
          </Empty>
        ) : (
          <Tb testId="hub-leads-table"
              head={<>
                <Th style={{ width: 38 }}>
                  <input type="checkbox" aria-label="Select all"
                         checked={shown.length > 0 && sel.length === shown.length}
                         onChange={(e) => setSel(e.target.checked ? shown.map((r) => r.id) : [])}
                         style={{ accentColor: "var(--accent)", width: 14, height: 14 }}
                         data-testid="hub-leads-select-all" />
                </Th>
                <Th style={{ width: "27%" }}>Case</Th>
                <Th style={{ width: "19%" }}>Introduced by</Th>
                <Th style={{ width: "16%" }}>Stage</Th>
                <Th className="r" style={{ width: "13%" }}>Facility</Th>
                <Th className="r" style={{ width: "9%" }}>Desks</Th>
                <Th className="r" style={{ width: "11%" }}>Opened</Th>
              </>}>
            {shown.map((l) => {
              const meta = caseState(l);
              return (
                <tr key={l.id} className={l.stage === "declined" ? "flag" : undefined} style={{ cursor: "pointer" }}
                    onClick={openRow(nav, `/hub/leads/${l.id}`)} data-testid={`hub-lead-row-${l.id}`}>
                  <Td className="m-check" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={sel.includes(l.id)}
                           onChange={(e) => setSel((s) => e.target.checked ? [...s, l.id] : s.filter((x) => x !== l.id))}
                           style={{ accentColor: "var(--accent)", width: 14, height: 14 }}
                           data-testid={`hub-lead-select-${l.id}`} />
                  </Td>
                  <Td className="lead" style={{ padding: "20px" }}>
                    <Link to={`/hub/leads/${l.id}`} style={{ textDecoration: "none" }}>
                      <CellMain style={{ fontSize: 15 }}>{l.business_name || l.applicant_name}</CellMain>
                    </Link>
                    <CellSub>
                      {l.applicant_name} · {l.finance_type}{l.reference ? ` · ${l.reference}` : ""}
                    </CellSub>
                    <div><CaseStamp at={l.created_at} testId={`hub-lead-stamp-${l.id}`} /></div>
                    <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 8 }}>
                      <CustomerTag lead={l} />
                      <FastTrackTag lead={l} testId={`hub-lead-fast-track-${l.id}`} />
                      <DirectTag lead={l} />
                      <ApiTag lead={l} />
                      {l.archived && (
                        <span data-testid={`lead-archived-chip-${l.id}`}
                              title={l.archived_reason || "Out of the live book"}>
                          <Tag>Archived</Tag>
                        </span>
                      )}
                    </div>
                  </Td>
                  <Td data-label="Introduced by">
                    <CellMain style={{ fontSize: 13.5 }}>
                      {l.direct ? "Connexis Direct" : (l.source_name || l.source_type || "In-house")}
                    </CellMain>
                    <CellSub>
                      {l.direct ? "Website application" : l.submitted_by_name
                        ? <span data-testid={`hub-lead-seat-${l.id}`}>via <b style={{ color: "var(--ink)" }}>{l.submitted_by_name}</b> · seat</span>
                        : l.source_type}
                    </CellSub>
                  </Td>
                  <Td data-label="Stage">
                    <StatusChip state={meta} testId={`hub-lead-stage-${l.id}`} />
                    {l.stage === "declined" ? <PassedNote lead={l} /> : meta.sub && <CellSub style={{ marginTop: 6 }}>{meta.sub}</CellSub>}
                    {meta.key === "awaiting_docs" && <div><DocsProgress progress={l.docs_progress} variant="mini" testId={`hub-lead-docs-progress-${l.id}`} /></div>}
                  </Td>
                  <Td className="r m-third" data-label="Facility"><Money v={l.amount_gbp} /></Td>
                  <Td className="r m-third" data-label="Desks"><Money raw={String(l.routes?.length || 0)} tone={l.routes?.length ? "ink" : "muted"} /></Td>
                  <Td className="r m-third" data-label="Opened"><div className="date">{ukDay(l.created_at, "—")}</div></Td>
                </tr>
              );
            })}
          </Tb>
        )}
      </Panel>

      <NewLeadDialog open={open} onOpenChange={setOpen} onCreated={(id) => (id ? nav(`/hub/leads/${id}`) : load())} />
    </div>
  );
}

function NewLeadDialog({ open, onOpenChange, onCreated }) {
  const EMPTY = { applicant_name: "", business_name: "", finance_type: FINANCE_TYPES[0], amount_gbp: "", source_type: "in-house", source_name: "", source_partner_id: "", applicant_email: "", applicant_phone: "", notes: "",
                  company_number: "", company_address: "", incorporation_date: "", industry_sector: "", vat_number: "", employees: "", annual_revenue_gbp: "" };
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [referrers, setReferrers] = useState([]);
  const [directors, setDirectors] = useState([]);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const extras = [["incorporation_date", "incorporation date"], ["industry_sector", "sector"], ["vat_number", "VAT"],
                  ["employees", "employees"], ["annual_revenue_gbp", "revenue"]].filter(([k]) => form[k]).map(([, w]) => w);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setDirectors([]);
    api.get("/hub/referrers").then((r) => setReferrers(r.data.items || [])).catch(() => {});
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.applicant_name.trim() || !form.amount_gbp) { toast.error("Applicant name and amount required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, amount_gbp: parseFloat(form.amount_gbp) || 0 };
      if (payload.source_type !== "referral") payload.source_partner_id = null;
      if (!payload.source_partner_id) delete payload.source_partner_id;
      if (payload.employees !== "") payload.employees = parseInt(payload.employees, 10);
      if (payload.annual_revenue_gbp !== "") payload.annual_revenue_gbp = parseFloat(payload.annual_revenue_gbp);
      for (const k of ["company_number", "company_address", "incorporation_date", "industry_sector", "vat_number", "employees", "annual_revenue_gbp"]) {
        if (payload[k] === "" || Number.isNaN(payload[k])) delete payload[k];
      }
      const r = await api.post("/hub/leads", payload);
      toast.success("Case created — route it to a desk next");
      onOpenChange(false);
      onCreated?.(r.data?.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="hub-new-lead-dialog">
        <DialogHeader>
          <DialogTitle>New case</DialogTitle>
          <DialogDescription>Enter the applicant details. Route it to desks on the case record.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <CompanyLookup testId="hub-lead-company-lookup" onPick={(c) => {
            const vals = {
              business_name: c.business_name, company_number: c.company_number, company_address: c.company_address,
              incorporation_date: c.incorporation_date, industry_sector: c.industry_sector, vat_number: c.vat_number,
              employees: c.employees, annual_revenue_gbp: c.annual_revenue_gbp,
            };
            setForm((f) => fillEmpty(f, vals).next);
            setDirectors(c.directors || []);
            return fillEmpty(form, vals).filled;
          }} />
          <DirectorPick directors={directors} value={form.applicant_name} testId="hub-lead-director" title="Who's the applicant?"
                        note="The company's directors, from Connexis search — tap one, or type the name below."
                        onPick={(name) => setForm((f) => ({ ...f, applicant_name: name }))} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Applicant name *" testId="lead-applicant"><Input value={form.applicant_name} onChange={setField("applicant_name")} /></Field>
            <Field label="Business name"><Input value={form.business_name} onChange={setField("business_name")} data-testid="lead-business-name" /></Field>
            <Field label="Companies House #"><Input value={form.company_number} onChange={setField("company_number")} placeholder="Runs the Company Check" data-testid="lead-company-number" /></Field>
            <Field label="Registered address"><Input value={form.company_address} onChange={setField("company_address")} data-testid="lead-company-address" /></Field>
            <Field label="Finance type *">
              <Select value={form.finance_type} onValueChange={(v) => setForm((f) => ({ ...f, finance_type: v }))}>
                <SelectTrigger data-testid="lead-finance-type"><SelectValue /></SelectTrigger>
                <SelectContent>{FINANCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Amount requested *"><MoneyField value={form.amount_gbp} onChange={(v) => setForm((f) => ({ ...f, amount_gbp: v }))} presets={DEAL_PRESETS} testId="lead-amount" compact hint="drag or type — no ceiling" /></Field>
            <Field label="Source">
              <Select value={form.source_type} onValueChange={(v) => setForm((f) => ({ ...f, source_type: v }))}>
                <SelectTrigger data-testid="lead-source-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in-house">In-house</SelectItem>
                  <SelectItem value="referral">Referral partner</SelectItem>
                  <SelectItem value="ad-campaign">Ad campaign</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.source_type === "referral" ? (
              <Field label="Referral partner">
                <Select value={form.source_partner_id} onValueChange={(v) => setForm((f) => ({ ...f, source_partner_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Pick a partner" /></SelectTrigger>
                  <SelectContent>{referrers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            ) : (
              <Field label="Source name / campaign"><Input value={form.source_name} onChange={setField("source_name")} placeholder="e.g. Google Ads · UK Bridging" /></Field>
            )}
            <Field label="Applicant email"><Input type="email" value={form.applicant_email} onChange={setField("applicant_email")} /></Field>
            <Field label="Applicant phone"><Input value={form.applicant_phone} onChange={setField("applicant_phone")} /></Field>
          </div>
          <Field label="Notes"><Textarea value={form.notes} onChange={setField("notes")} className="min-h-[80px]" /></Field>
          {extras.length > 0 && (
            <p className="text-[12px] text-slate-500" data-testid="hub-lead-extras">
              Also filled from Connexis search: {extras.join(", ")} — you can edit these on the case.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="hub-lead-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="hub-lead-submit">
              {busy ? "Creating…" : "Create the case"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
